import React from 'react';
import { View, Text, ScrollView, Pressable, Platform } from 'react-native';
import { AntDesign, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles from '@stylesheet';
import { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { LineChart, BarChart, DonutChart, AreaChart } from '@utils/chartUtils/createChart';

const AnalyticsDemoData = () => {
  const theme = themeSelector();
  const cardStyle = getModernCardStyle(theme);
  const textColor = getModernTextColor(theme, 'primary');
  const mutedTextColor = getModernTextColor(theme, 'secondary');
  const successColor = '#22c55e';
  const dangerColor = '#ef4444';
  const primaryColor = '#3b82f6';

  // Demo data for analytics
  const demoMetrics = {
    totalDatasets: 156,
    totalItems: 12847,
    totalViews: 89234,
    totalInteractions: 3456,
    growthRate: 23.5
  };

  const timeSeriesData = [
    { x: '2024-09-01', y: 1200 },
    { x: '2024-09-05', y: 1890 },
    { x: '2024-09-10', y: 2300 },
    { x: '2024-09-15', y: 2800 },
    { x: '2024-09-20', y: 3200 },
    { x: '2024-09-25', y: 3800 },
    { x: '2024-09-30', y: 4200 }
  ];

  const departmentData = {
    series: [30, 25, 20, 15, 10],
    labels: ['Sales', 'Marketing', 'HR', 'Finance', 'IT']
  };

  const folderComparisonData = [
    { x: 'Customer Data', y: 2340 },
    { x: 'Product Catalog', y: 1890 },
    { x: 'Sales Reports', y: 3200 },
    { x: 'Marketing Analytics', y: 1456 },
    { x: 'Financial Records', y: 2890 }
  ];

  const StatCard = ({ title, value, icon, color, growth }: any) => (
    <Animatable.View animation="fadeInUp" duration={600} style={[styles.modernStatsCard, cardStyle, { flex: 1, marginRight: 12, marginBottom: 16 }]}>
      <View style={styles.modernRow}>
        <View style={[styles.modernActionButton, getModernActionStyle(theme, color), { width: 40, height: 40 }]}>
          {icon}
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={[styles.modernFieldLabel, { color: mutedTextColor, fontSize: 12 }]}>{title}</Text>
          <Text style={[styles.modernStatsNumber, { color: textColor, fontSize: 18, fontWeight: '700' }]}>
            {value}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
            <MaterialIcons 
              name={growth > 0 ? 'trending-up' : 'trending-down'} 
              size={12} 
              color={growth > 0 ? successColor : dangerColor} 
            />
            <Text style={[{ 
              fontSize: 11, 
              color: growth > 0 ? successColor : dangerColor, 
              marginLeft: 4,
              fontWeight: '600'
            }]}>
              {growth > 0 ? '+' : ''}{growth}%
            </Text>
          </View>
        </View>
      </View>
    </Animatable.View>
  );

  return (
    <ScrollView style={{ flex: 1, padding: 16 }} showsVerticalScrollIndicator={false}>
      <Animatable.View animation="fadeInDown" duration={600}>
        <Text style={[{ fontSize: 24, fontWeight: '700', color: textColor, marginBottom: 8 }]}>
          Analytics Demo Dashboard
        </Text>
        <Text style={[{ fontSize: 16, color: mutedTextColor, marginBottom: 20 }]}>
          Sample data visualization showcasing analytics capabilities
        </Text>
      </Animatable.View>

      {/* Summary Cards */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 20 }}>
        <View style={{ width: Platform.OS === 'web' ? '24%' : '48%', marginRight: '1%' }}>
          <StatCard
            title="Total Datasets"
            value={demoMetrics.totalDatasets.toLocaleString()}
            icon={<FontAwesome5 name="database" size={20} color="#ffffff" />}
            color="blue"
            growth={23.5}
          />
        </View>
        <View style={{ width: Platform.OS === 'web' ? '24%' : '48%', marginRight: '1%' }}>
          <StatCard
            title="Total Items"
            value={demoMetrics.totalItems.toLocaleString()}
            icon={<MaterialIcons name="insert-chart" size={20} color="#ffffff" />}
            color="green"
            growth={12.8}
          />
        </View>
        <View style={{ width: Platform.OS === 'web' ? '24%' : '48%', marginRight: '1%' }}>
          <StatCard
            title="Total Views"
            value={demoMetrics.totalViews.toLocaleString()}
            icon={<FontAwesome5 name="eye" size={20} color="#ffffff" />}
            color="amber"
            growth={8.3}
          />
        </View>
        <View style={{ width: Platform.OS === 'web' ? '24%' : '48%' }}>
          <StatCard
            title="Interactions"
            value={demoMetrics.totalInteractions.toLocaleString()}
            icon={<MaterialIcons name="touch-app" size={20} color="#ffffff" />}
            color="red"
            growth={15.7}
          />
        </View>
      </View>

      {/* Charts Section */}
      <View style={{ flexDirection: Platform.OS === 'web' ? 'row' : 'column', marginBottom: 20 }}>
        {/* Time Series Chart */}
        <View style={{ flex: Platform.OS === 'web' ? 2 : 1, marginRight: Platform.OS === 'web' ? 16 : 0, marginBottom: 16 }}>
          <Animatable.View animation="fadeInLeft" duration={600} style={[cardStyle, { padding: 20 }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor }]}>Data Activity Trends</Text>
              <View style={{ flexDirection: 'row' }}>
                <Pressable style={[styles.modernButton, getModernActionStyle(theme, 'blue'), { marginRight: 8 }]}>
                  <Text style={[{ fontSize: 12, color: '#ffffff' }]}>Views</Text>
                </Pressable>
                <Pressable style={[styles.modernButton, getModernActionStyle(theme, 'grey')]}>
                  <Text style={[{ fontSize: 12, color: mutedTextColor }]}>Items</Text>
                </Pressable>
              </View>
            </View>

            <View style={{ height: 250 }}>
              <AreaChart 
                seriesData={timeSeriesData.map(item => ({ x: item.x, y: item.y }))}
                categories={timeSeriesData.map(item => item.x)}
                chartColor={primaryColor}
              />
            </View>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 }}>
              <View>
                <Text style={[{ fontSize: 24, fontWeight: '700', color: textColor }]}>4,200</Text>
                <Text style={[{ fontSize: 12, color: mutedTextColor }]}>Current Month</Text>
              </View>
              <View style={[{ backgroundColor: successColor + '20', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 12 }]}>
                <Text style={[{ fontSize: 12, color: successColor, fontWeight: '600' }]}>↗️ +23.5% Growth</Text>
              </View>
            </View>
          </Animatable.View>
        </View>

        {/* Department Analytics */}
        <View style={{ flex: Platform.OS === 'web' ? 1 : 1 }}>
          <Animatable.View animation="fadeInRight" duration={600} style={[cardStyle, { padding: 20 }]}>
            <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor, marginBottom: 16 }]}>
              Department Breakdown
            </Text>

            <View style={{ height: 200, marginBottom: 16 }}>
              <DonutChart 
                series={departmentData.series} 
                labels={departmentData.labels}
              />
            </View>

            <View>
              {departmentData.labels.map((dept, index) => (
                <View key={dept} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: 6, 
                      backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                      marginRight: 8 
                    }} />
                    <Text style={[{ fontSize: 14, color: textColor }]}>{dept}</Text>
                  </View>
                  <Text style={[{ fontSize: 14, color: mutedTextColor, fontWeight: '600' }]}>
                    {departmentData.series[index]}%
                  </Text>
                </View>
              ))}
            </View>
          </Animatable.View>
        </View>
      </View>

      {/* Folder Comparison Chart */}
      <Animatable.View animation="fadeInUp" duration={600} style={[cardStyle, { padding: 20, marginBottom: 20 }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={[{ fontSize: 18, fontWeight: '600', color: textColor }]}>Folder Performance Comparison</Text>
          <Pressable style={[styles.modernButton, getModernActionStyle(theme, 'grey')]}>
            <Text style={[{ fontSize: 12, color: mutedTextColor }]}>Last 30 days</Text>
          </Pressable>
        </View>

        <View style={{ height: 250 }}>
          <BarChart
            seriesData={folderComparisonData}
            categories={folderComparisonData.map(item => item.x)}
            colors={[primaryColor]}
          />
        </View>

        <View style={{ marginTop: 16 }}>
          <Text style={[{ fontSize: 14, color: mutedTextColor, textAlign: 'center' }]}>
            Performance based on view count and user interactions
          </Text>
        </View>
      </Animatable.View>

      {/* Insights Cards */}
      <View style={{ flexDirection: Platform.OS === 'web' ? 'row' : 'column' }}>
        <View style={{ flex: 1, marginRight: Platform.OS === 'web' ? 8 : 0, marginBottom: 16 }}>
          <Animatable.View animation="fadeInLeft" duration={600} delay={200} style={[cardStyle, { padding: 20 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <MaterialIcons name="insights" size={24} color={primaryColor} />
              <Text style={[{ fontSize: 16, fontWeight: '600', color: textColor, marginLeft: 8 }]}>
                Key Insights
              </Text>
            </View>
            <Text style={[{ fontSize: 14, color: mutedTextColor, lineHeight: 20 }]}>
              • Sales department shows highest data engagement{'\n'}
              • Customer data folder receives 40% more views{'\n'}
              • Processing time improved by 15% this month{'\n'}
              • Peak activity hours: 10 AM - 2 PM
            </Text>
          </Animatable.View>
        </View>

        <View style={{ flex: 1, marginLeft: Platform.OS === 'web' ? 8 : 0 }}>
          <Animatable.View animation="fadeInRight" duration={600} delay={400} style={[cardStyle, { padding: 20 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <MaterialIcons name="trending-up" size={24} color={successColor} />
              <Text style={[{ fontSize: 16, fontWeight: '600', color: textColor, marginLeft: 8 }]}>
                Predictions
              </Text>
            </View>
            <Text style={[{ fontSize: 14, color: mutedTextColor, lineHeight: 20 }]}>
              • Expected 25% growth in next quarter{'\n'}
              • Marketing folder likely to double in size{'\n'}
              • Optimal time for system scaling: Q1 2025{'\n'}
              • Confidence level: 78%
            </Text>
          </Animatable.View>
        </View>
      </View>
    </ScrollView>
  );
};

export default AnalyticsDemoData;
